import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as gridUtils from "grid/utils";
import { ToggleAllRows } from "grid/toggleAllRows";
import { UuidTextFilter } from "grid/uuid_text_filter";
import {
  AMOUNT_COL_DEF,
  DEFAULT_COL_DEF,
  ACTIONS_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
  TEXT_FILTER_COL_DEF,
} from "grid/columns";
import { BulkAction } from "grid/bulk_action";
import * as rowActions from "./row_actions";
import { GridController } from "grid/grid_controller";
import { RefundRequest } from "../../types/refund_request";
import { StatusCheckboxFilter } from "grid/status_checkbox_filter";

enum Action {}

interface RefundRequestModalData {
  node: AgGrid.IRowNode<RefundRequest>;
  gridApi: AgGrid.GridApi<RefundRequest>;
  originalActions: string;
}

const refundRequestUpdateModalDataStore = new Map<string, RefundRequestModalData>();

function openRefundRequestUpdateModal(
  params: AgGrid.CellClickedEvent<RefundRequest>,
  node: AgGrid.IRowNode<RefundRequest>,
  gridApi: AgGrid.GridApi<RefundRequest>,
  actionType: string,
): void {
  const refundRequestId = params.data.id;
  const requestedAmount = params.data.requested_amount;

  const originalActions = node.data.actions || "";

  refundRequestUpdateModalDataStore.set(refundRequestId, {
    node,
    gridApi,
    originalActions,
  });

  const url = `/payments/refund_requests/${encodeURIComponent(refundRequestId)}/edit?type=${encodeURIComponent(actionType)}&requested_amount=${encodeURIComponent(requestedAmount)}`;

  const turboFrame = document.querySelector("turbo-frame#modal") as HTMLElement & { src?: string };
  if (turboFrame) {
    turboFrame.src = url;
  }
}

export function getRefundRequestUpdateModalData(refundRequestId: string): RefundRequestModalData | undefined {
  return refundRequestUpdateModalDataStore.get(refundRequestId);
}

export function removeRefundRequestUpdateModalData(refundRequestId: string): void {
  refundRequestUpdateModalDataStore.delete(refundRequestId);
}

export default class extends GridController<RefundRequest> {
  static values = Object.assign(super.values, {
    isSelectionEnabled: Boolean,
  });

  declare readonly gridTarget: HTMLInputElement;
  declare readonly hasGridTarget: boolean;
  declare readonly hasSearchInputTarget: boolean;
  declare readonly totalPaymentAmountTarget: HTMLInputElement;
  declare readonly isSelectionEnabledValue: boolean;

  bulkPayAction?: BulkAction<RefundRequest>;

  connect() {
    super.connect();
  }

  override buildGrid() {
    const ctrl = this;
    const { i18nJson, paginationPageSize } = this.gridTarget.dataset;
    const grantsStr = this.gridTarget.dataset.userGrants || this.gridTarget.dataset.userGrant || "";
    const grants = grantsStr
      .split(",")
      .map((g) => g.trim())
      .filter(Boolean);
    const isPlatformOperator = grants.includes("PLATFORM_OPERATOR");
    const isGroupOperator = grants.includes("GROUP_OPERATOR");
    const showSellerName = isPlatformOperator || isGroupOperator;

    const i18n = JSON.parse(i18nJson);
    const refundStatusValues = ["PENDING", "APPROVED", "PROCESSING", "COMPLETED", "REJECTED", "FAILED"];
    const refundStatusLabels = Object.fromEntries(
      refundStatusValues.map((code) => [code, i18n[`status_${code.toLowerCase()}`] || code]),
    );

    const handleAccept = (
      params: AgGrid.CellClickedEvent<RefundRequest>,
      node: AgGrid.IRowNode<RefundRequest>,
      gridApi: AgGrid.GridApi<RefundRequest>,
    ) => {
      openRefundRequestUpdateModal(params, node, gridApi, "accept");
    };
    const handleReject = (
      params: AgGrid.CellClickedEvent<RefundRequest>,
      node: AgGrid.IRowNode<RefundRequest>,
      gridApi: AgGrid.GridApi<RefundRequest>,
    ) => {
      openRefundRequestUpdateModal(params, node, gridApi, "reject");
    };

    const colDefs: AgGrid.ColDef[] = [
      ...(ctrl.isSelectionEnabledValue ? [{ width: 36, checkboxSelection: true, headerComponent: ToggleAllRows }] : []),
      {
        field: "payment_id",
        ...DEFAULT_COL_DEF,
        filter: "uuidTextFilter",
        sortable: false,
        filterParams: {
          locales: {
            ...(i18n?.grid?.uuid_text_filter || {}),
            ...(i18n?.grid?.dropdown_filter || {}),
          },
        },
      },
      ...(showSellerName
        ? [{ field: "seller_name", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: false }]
        : []),
      { field: "order_id", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: false },
      {
        field: "accepted_amount",
        colId: "accepted_amount",
        ...AMOUNT_COL_DEF,
        ...AMOUNT_FILTER_COL_DEF,
        sortable: true,
      },
      {
        field: "requested_amount",
        colId: "requested_amount",
        ...AMOUNT_COL_DEF,
        ...AMOUNT_FILTER_COL_DEF,
        sortable: true,
      },
      {
        field: "created_at",
        colId: "created_at",
        ...DEFAULT_COL_DEF,
        ...DATE_FILTER_COL_DEF,
        sortable: true,
        sort: "desc",
        hide: true,
      },
      { field: "updated_at", colId: "updated_at", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true },
      {
        field: "status",
        ...DEFAULT_COL_DEF,
        filter: "statusCheckboxFilter",
        filterParams: {
          values: refundStatusValues,
          labels: refundStatusLabels,
          locales: i18n?.grid?.dropdown_filter || {},
        },
        sortable: true,
        cellClass: (params: AgGrid.CellClassParams<RefundRequest>) => {
          const status = params.data?.status_code?.toUpperCase();
          const baseClasses = (DEFAULT_COL_DEF.cellClass as string[]) || [];
          switch (status) {
            case "PENDING":
              return [...baseClasses, "status-pending"];
            case "PROCESSING":
              return [...baseClasses, "status-processing"];
            case "ACCEPTED":
              return [...baseClasses, "status-accepted"];
            case "REJECTED":
              return [...baseClasses, "status-rejected"];
            case "FAILED":
              return [...baseClasses, "status-failed"];
            case "COMPLETED":
              return [...baseClasses, "status-completed"];
            default:
              return baseClasses;
          }
        },
        cellStyle: (params: any): AgGrid.CellStyle => {
          const status = params.data?.status_code?.toUpperCase();
          const baseStyles =
            typeof DEFAULT_COL_DEF.cellStyle === "function"
              ? DEFAULT_COL_DEF.cellStyle(params)
              : DEFAULT_COL_DEF.cellStyle || {};
          switch (status) {
            case "PENDING":
              return {
                ...baseStyles,
                backgroundColor: "#DBEAFE",
                color: "#1E40AF",
                fontWeight: "600",
              };
            case "PROCESSING":
              return {
                ...baseStyles,
                backgroundColor: "#FEF3C7",
                color: "#92400E",
                fontWeight: "600",
              };
            case "ACCEPTED":
              return {
                ...baseStyles,
                backgroundColor: "#D1FAE5",
                color: "#065F46",
                fontWeight: "600",
              };
            case "REJECTED":
              return {
                ...baseStyles,
                backgroundColor: "#FCA5A5",
                color: "#7F1D1D",
                fontWeight: "600",
              };
            case "FAILED":
              return {
                ...baseStyles,
                backgroundColor: "#FEE2E2",
                color: "#991B1B",
                fontWeight: "600",
              };
            case "COMPLETED":
              return {
                ...baseStyles,
                backgroundColor: "#86EFAC",
                color: "#14532D",
                fontWeight: "600",
              };
            default:
              return baseStyles;
          }
        },
      },
      {
        ...DEFAULT_COL_DEF,
        sortable: false,
        cellRenderer: "reasonsCellRenderer",
        cellClass: ["ag-left-aligned-cell", ...(DEFAULT_COL_DEF.cellClass as string[])],
        headerName: i18n["reasons"] || "Reasons",
        cellRendererParams: {
          requestReasonLabel: i18n["request_reason_label"] || "Request Reason",
          reasonLabel: i18n["reason_label"] || "Reason",
          statusReasonLabel: i18n["status_reason_label"] || "Status Reason",
          failMessageLabel: i18n["fail_message_label"] || "Fail Message",
        },
        minWidth: 300,
        flex: 2,
      },
      ...(isPlatformOperator
        ? [
            {
              ...ACTIONS_COL_DEF,
              cellClass: "actions-cell",
              onCellClicked: (params) => {
                rowActions.onActionCellClicked(params, async function (action) {
                  gridApi.forEachNode((node) => {
                    if (node.data.id == params.data.id) {
                      switch (action) {
                        case rowActions.Action.REJECT:
                          handleReject(params, node, gridApi);
                          break;
                        case rowActions.Action.ACCEPT:
                          handleAccept(params, node, gridApi);
                          break;
                      }
                    }
                  });
                });
              },
            },
          ]
        : []),
    ];

    const dataSource = new datasource.Datasource({ callbacks: {}, limit: paginationPageSize });
    // prevent double-acting with checkbox
    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const options: AgGrid.GridOptions<RefundRequest> = {
      ...defaultOptions,
      components: {
        ...(defaultOptions.components || {}),
        statusCheckboxFilter: StatusCheckboxFilter,
        uuidTextFilter: UuidTextFilter,
      },
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<RefundRequest, any>),
      },
      datasource: dataSource,
      editType: "fullRow",
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n }),
      pagination: true,
      paginationPageSize: parseInt(paginationPageSize),
    };

    const selectionOptions: AgGrid.GridOptions<RefundRequest> = ctrl.isSelectionEnabledValue
      ? {
          rowSelection: "multiple",
          suppressRowClickSelection: true,
          suppressRowDeselection: true,
          onRowSelected(event) {
            ctrl.bulkPayAction.onRowSelected(event);
          },
        }
      : {};

    const gridApi = AgGrid.createGrid(this.gridTarget, { ...options, ...selectionOptions });
    dataSource.gridApi = gridApi;
    return gridApi;
  }
}
